import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Sun, Sunset, Moon, Printer, Plus } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useOrariTurni, orarioLabel } from "../lib/orari";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ScriviConsegnaNew } from "./ScriviConsegnaNew";

const TURNI = [["mattina", "Mattina", Sun], ["pomeriggio", "Pomeriggio", Sunset], ["notte", "Notte", Moon]];
const TL = { mattina: "Mattina", pomeriggio: "Pomeriggio", notte: "Notte" };
export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hm = (s) => { const [h, m] = String(s || "00:00").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const fmtG = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "2-digit" });

export const turnoAdesso = (orari) => {
  const now = new Date(); const m = now.getHours() * 60 + now.getMinutes();
  const sm = hm(orari.mattina?.inizio || "07:00"), sp = hm(orari.pomeriggio?.inizio || "14:00"), sn = hm(orari.notte?.inizio || "20:00");
  if (m >= sm && m < sp) return { data: ymd(now), turno: "mattina" };
  if (m >= sp && m < sn) return { data: ymd(now), turno: "pomeriggio" };
  if (m < sm) now.setDate(now.getDate() - 1);
  return { data: ymd(now), turno: "notte" };
};

export const VistaSwitch = ({ vista, setVista }) => (
  <div className="inline-flex rounded-lg border overflow-hidden mb-4" data-testid="consegne-vista-switch">
    {[["attuale", "Vista attuale"], ["new", "Vista new"], ["ai", "Vista AI"]].map(([k, l]) => (
      <button key={k} onClick={() => setVista(k)} data-testid={`consegne-vista-${k}`}
        className={`px-4 h-9 text-sm font-semibold transition-colors ${vista === k ? "bg-primary text-white" : "bg-white hover:bg-secondary"}`}>{l}</button>
    ))}
  </div>
);

export const NomiBlu = ({ testo, nomi }) => {
  if (!nomi?.length) return testo;
  const rx = new RegExp(`(${nomi.map(esc).join("|")})`, "g");
  return testo.split(rx).map((p, i) => (nomi.includes(p) ? <span key={i} className="font-semibold text-blue-700">{p}</span> : p));
};

function Prosa({ d, sel }) {
  return (
    <div className="gr-card p-5 sm:p-6 max-w-4xl" data-testid="vn-prosa">
      <div className="flex flex-wrap items-center gap-2 pb-3 border-b">
        <Bot size={18} className="text-slate-600" /><b className="text-lg" data-testid="vn-prosa-titolo">Consegna del turno di {TL[sel.turno]}</b>
        <span className="text-sm text-muted-foreground">{fmtG(sel.data).charAt(0).toUpperCase() + fmtG(sel.data).slice(1)} · scritta dal sistema con i dati registrati e le note degli operatori, senza costi</span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => window.print()} data-testid="vn-stampa"><Printer size={14} className="mr-1" /> Stampa</Button>
      </div>
      <div className="text-[15px] sm:text-[16px] leading-7 text-slate-800">
        {d.sezioni.map((s, i) => (
          <section key={s.titolo} className="mt-5" data-testid={`vn-sez-${i}`}>
            <h3 className="font-bold text-[17px] mb-1">{s.titolo}</h3>
            {s.paragrafi.map((p, j) => <p key={j} className="mb-2"><NomiBlu testo={p} nomi={d.nomi} /></p>)}
          </section>
        ))}
      </div>
      <p className="text-sm text-slate-500 mt-6 pt-3 border-t" data-testid="vn-firme">Note scritte da: {d.operatori.length ? d.operatori.join(", ") : "nessun operatore in questo turno"}</p>
    </div>
  );
}

export default function ConsegneVistaNew({ structureId }) {
  const orari = useOrariTurni(structureId);
  const { structures } = useAuth();
  const qc = useQueryClient();
  const [sel, setSel] = useState(() => turnoAdesso(orari));
  const [scrivi, setScrivi] = useState(false);
  const { data: d, isLoading, error } = useQuery({
    queryKey: ["consegne-vista-new", sel.data, sel.turno, structureId],
    queryFn: async () => (await api.get("/consegne/vista-new", { params: { data: sel.data, turno: sel.turno, structure_id: structureId } })).data,
    refetchInterval: 60000,
  });
  return (
    <div className="space-y-3" data-testid="consegne-vista-new">
      <div className="grid grid-cols-3 sm:flex sm:flex-wrap items-center gap-2">
        {TURNI.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setSel((p) => ({ ...p, turno: k }))} data-testid={`vn-turno-${k}`}
            className={`inline-flex flex-wrap justify-center items-center gap-x-1.5 rounded-lg border px-2 sm:px-3 py-1 min-h-9 text-sm font-semibold ${sel.turno === k ? "bg-primary text-white border-primary" : "bg-white hover:bg-secondary"}`}>
            <Icon size={14} /> {l} <span className="font-normal opacity-75 text-xs">{orarioLabel(orari[k])}</span>
          </button>
        ))}
        <Input type="date" value={sel.data} max={ymd(new Date())} onChange={(e) => e.target.value && setSel((p) => ({ ...p, data: e.target.value }))} className="col-span-3 sm:w-44 h-9" data-testid="vn-data" />
        <Button onClick={() => setScrivi(true)} className="font-semibold col-span-3 sm:col-span-1 sm:ml-auto" data-testid="vn-scrivi"><Plus size={16} className="mr-1" /> Scrivi consegna</Button>
      </div>
      {isLoading && <div className="gr-card p-6 text-sm text-muted-foreground">Caricamento…</div>}
      {error && <div className="gr-card p-6 text-sm text-red-700" data-testid="vn-errore">{error?.response?.data?.detail || "Errore nel caricamento"}</div>}
      {d && <Prosa d={d} sel={sel} />}
      <ScriviConsegnaNew open={scrivi} onClose={() => setScrivi(false)} structureId={structureId} structures={structures} orari={orari}
        onSaved={() => qc.invalidateQueries({ queryKey: ["consegne-vista-new"] })} />
    </div>
  );
}
